class QuantityInput extends HTMLElement {
  constructor() {
    super();
    // Note, the event listeners are deliberately set in the constructor to avoid adding
    // multiple event listeners when the component is re-rendered.
    this.initVariables();
    this.setEventListeners();
    this.disableAndEnableButtons(); // Note that we use JS to disable and enable the buttons rather than in liquid because there are instances such as subscription product cards where we pull the quantity from sessionStorage.
  }

  /**
   * Initialize the variables used in the class
   */
  initVariables() {
    this.input = this.querySelector('[data-quantity-input]');
    this.maxQuantity = Number.parseInt(this.input.getAttribute('max'), 10);
    this.minQuantity = Number.parseInt(this.input.getAttribute('min'), 10);
    this.defaultValue = Number.parseInt(this.input.getAttribute('value'), 10);
    this.plusButton = this.querySelector('button[name="plus"]');
    this.minusButton = this.querySelector('button[name="minus"]');
  }

  setEventListeners = () => {
    this.input.addEventListener('input', event => {
      const quantity = Number.parseInt(event.target.value, 10);
      this.afterValueChange(quantity);
    });
    this.plusButton.addEventListener('click', event => {
      this.increaseValue(event);
    });
    this.minusButton.addEventListener('click', event => {
      this.decreaseValue(event);
    });
  };

  /**
   * Methods to call after the value of the input has changed
   * @param {Number} quantity
   * @return {void}
   */
  afterValueChange(quantity) {
    this.disableAndEnableButtons();
    this.dispatchQuantityChangeEvent(quantity);
  }

  /**
   * Updates the quantity input field with the given value and
   * enables or disables the relevant buttons based on the new quantity.
   * This method does not emit the "pxl:quantityChange" event.
   *
   * @param {number} quantity - The new quantity to set in the input field.
   */
  updateQuantityWithoutEmittingEvent(quantity) {
    this.input.value = quantity;
    this.disableAndEnableButtons();
  }

  /**
   * Increase the input value by one
   * @return {void}
   */
  increaseValue() {
    const quantity = Number.parseInt(this.input.value, 10) + 1;
    this.input.value = quantity;
    this.afterValueChange(quantity);
  }

  /**
   * Decrease the input value by one
   * @return {void}
   */
  decreaseValue() {
    const quantity = Number.parseInt(this.input.value, 10) - 1;
    this.input.value = quantity;
    this.afterValueChange(quantity);
  }

  /**
   * Dispatch the "pxl:quantityChange" event
   */
  dispatchQuantityChangeEvent(quantity) {
    const event = new CustomEvent('pxl:quantityChange', {
      bubbles: true,
      detail: { quantity }
    });
    this.dispatchEvent(event);
  }

  /**
   * Disable and enable buttons based on min and max quantity
   * @return {void}
   */
  disableAndEnableButtons() {
    this.minusButton.disabled = this.input.value <= this.minQuantity;
    this.plusButton.disabled = this.input.value >= this.maxQuantity;
  }

  /**
   * Updates the input value and triggers necessary updates
   * @param {number} newValue - The new quantity value
   */
  updateValue(newValue) {
    const quantity = Number.parseInt(newValue, 10);
    this.input.value = quantity;

    this.afterValueChange(quantity);
  }
}

customElements.get('quantity-input') ||
  customElements.define('quantity-input', QuantityInput);
